import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

const ErrorContainer = styled.div`
  padding: 2rem;
  margin: 2rem;
  border: 2px solid #e74c3c;
  border-radius: 8px;
  background-color: #fadbd8;
`;

const ErrorTitle = styled.h2`
  color: #c0392b;
  margin-bottom: 1rem;
`;

const ErrorMessage = styled.p`
  color: #721c24;
  margin-bottom: 1rem;
`;

const ErrorButton = styled.button`
  background-color: #e74c3c;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  margin-right: 1rem;

  &:hover {
    background-color: #c0392b;
  }
`;

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the entire application.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Store error info in state
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <ErrorContainer>
          <ErrorTitle>Something went wrong</ErrorTitle>
          <ErrorMessage>
            {this.props.showErrorDetails && this.state.error
              ? this.state.error.toString()
              : "An unexpected error occurred. Please try again."}
          </ErrorMessage>
          <ErrorButton onClick={this.handleReset}>Try Again</ErrorButton>
          <ErrorButton onClick={this.handleGoHome}>Go to Home</ErrorButton>
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  onError: PropTypes.func,
  onReset: PropTypes.func,
  showErrorDetails: PropTypes.bool,
};

ErrorBoundary.defaultProps = {
  showErrorDetails: process.env.NODE_ENV === "development",
};

export default ErrorBoundary;